import { useEffect, useMemo, useState } from "react";

import { ClipboardCopy } from "lucide-react";
import { toast } from "sonner";
import {
  TableWrapper,
  TableBody,
  TableRow,
  TableRowItem,
  TableFooter,
  TablePagination,
} from "@/components/ui/table";
import { Avatar } from "@fluentui/react-components";

import { AdminUser } from "@/queries/admin/users";
import { Button } from "@/components/ui/button";
import { useResetUserPassword } from "@/mutations/admin/reset-user-password";

const SORTABLE_FIELDS = ["name"] as const;
export type SortableField = (typeof SORTABLE_FIELDS)[number];

const USERS_PER_PAGE = 10;

const UsersTable = ({
  users,
  setSelectedUser,
}: {
  users?: AdminUser[];
  setSelectedUser: (v: AdminUser | undefined) => void;
}) => {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortableField>("name");
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  const sortedUsers: AdminUser[] | undefined = useMemo(() => {
    if (!users) {
      return;
    }

    switch (sortField) {
      case "name":
        return [...users].sort(
          (a, b) =>
            (a[sortField] ?? "").localeCompare(b[sortField] ?? "") *
            sortDirection,
        );
    }
  }, [users, sortField, sortDirection]);

  const numberOfPages = useMemo(() => {
    if (!sortedUsers) {
      return 1;
    }

    return Math.ceil(sortedUsers.length / USERS_PER_PAGE);
  }, [sortedUsers]);

  const currentPageUsers = useMemo(
    () =>
      sortedUsers?.slice((page - 1) * USERS_PER_PAGE, page * USERS_PER_PAGE),
    [sortedUsers, page],
  );

  const onHeadingClick = (field: SortableField) => {
    if (field !== sortField) {
      setSortField(field);
      setSortDirection(1);
    } else {
      setSortDirection((prev) => (prev === 1 ? -1 : 1));
    }
  };

  useEffect(() => {
    if (page > numberOfPages && page !== 1) {
      setPage(numberOfPages || 1);
    }
  }, [page, numberOfPages]);

  return (
    <div className="py-6 self-stretch flex flex-col lg:flex-row gap-x-6 gap-y-2">
      <div className="flex-1 min-w-0">
        <TableWrapper>
          <TableBody>
            <thead>
              <TableRow>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("name")}
                  sortDirection={
                    sortField === "name" ? sortDirection : undefined
                  }
                >
                  Name
                </TableRowItem>
                <TableRowItem heading>Email</TableRowItem>
                <TableRowItem heading>LinkedIn</TableRowItem>
                <TableRowItem heading>Phone Number</TableRowItem>
                <TableRowItem heading>Marketing Consent</TableRowItem>
                <TableRowItem heading>Admin?</TableRowItem>
                <TableRowItem heading></TableRowItem>
                {/* <TableRowItem heading button></TableRowItem> */}
              </TableRow>
            </thead>
            <tbody>
              {currentPageUsers?.map((user) => (
                <Row
                  key={`user-${user.id}`}
                  user={user}
                  selectUser={() => setSelectedUser(user)}
                />
              ))}
            </tbody>
          </TableBody>
          {numberOfPages > 1 ? (
            <TableFooter>
              <TablePagination
                currentPage={page}
                totalPages={numberOfPages}
                navigateFn={setPage}
              />
            </TableFooter>
          ) : null}
        </TableWrapper>
      </div>
    </div>
  );
};

const Row = ({
  user,
  //   selectUser,
}: {
  user: AdminUser;
  selectUser: () => void;
}) => {
  const { resetUserPassword, isResettingUserPassword } = useResetUserPassword();

  return (
    <TableRow>
      <TableRowItem>
        <div className="flex flex-row gap-3 items-center">
          <Avatar
            image={{ src: user.profilePictureLink }}
            name={user.name}
            size={40}
          />
          <span className="text-[#0C0D0D] text-sm font-medium">
            {user.name}
          </span>
        </div>
      </TableRowItem>
      <TableRowItem>
        {Boolean(user.email) && (
          <div
            className="text-md font-semibold text-accent hover:underline hover:cursor-pointer hover:text-accent underline-offset-2 ml-2"
            onClick={() => {
              void navigator.clipboard.writeText(user.email ?? "");
              toast.success("Email copied to clipboard");
            }}
          >
            <span className="flex flex-row items-center gap-0.5">
              {user.email}
              <ClipboardCopy size={16} />
            </span>
          </div>
        )}
      </TableRowItem>
      <TableRowItem>
        {Boolean(user.linkedIn) && (
          <a
            href={user.linkedIn}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-[#646cff] font-medium hover:underline"
          >
            Link
          </a>
        )}
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">{user.phoneNumber}</span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {user.marketingConsent ? "Yes" : "No"}
        </span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {user.isAdmin ? "Yes" : "No"}
        </span>
      </TableRowItem>
      <TableRowItem>
        <Button
          variant="outline"
          disabled={isResettingUserPassword}
          onClick={() => resetUserPassword(user.id)}
        >
          Reset Password
        </Button>
      </TableRowItem>
      {/* <TableRowItem>
        <EllipsisVertical
          className="cursor-pointer"
          size={16}
          stroke="#B1B9B8"
          onClick={selectUser}
        />
      </TableRowItem> */}
    </TableRow>
  );
};

export default UsersTable;
